import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import {
    Button,
    FieldError,
    Input,
    Label,
    ListBox,
    Select,
    Switch,
    TextField,
} from '@heroui/react';
import type { AccountType, NormalBalance } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { formatGel } from '../../utils';
import { AccountParentPicker } from './AccountParentPicker';
import {
    ACCOUNT_ROLE_OPTIONS,
    ACCOUNT_TYPE_OPTIONS,
    CODE_ERROR_KEY,
    NORMAL_BALANCE_OPTIONS,
    VAT_ROLE_OPTIONS,
    type AccountRoleChoice,
    type VatRoleChoice,
} from './accountFormMeta';
import type { AccountFormModel } from './useAccountForm';

type AccountFormViewProps = {
    form: AccountFormModel;
    onCancel: () => void;
};

/**
 * Literal ids for the ledger-role message, so `aria-describedby` on the Select
 * can point at whichever of the two is rendered. Hardcoded rather than drilled
 * in as a prop — the same rule the rest of this file follows for testids.
 */
const ACCOUNT_ROLE_HINT_ID = 'account-form-account-role-hint';
const ACCOUNT_ROLE_ERROR_ID = 'account-form-account-role-error-text';

/** Inline "why is this locked" note under a frozen structural field. */
const LockNote: React.FC<{ testId: string }> = ({ testId }) => {
    const { t } = useTranslation('pages');
    return (
        <span
            className="flex items-center gap-1 text-xs text-muted"
            {...getNodeTestIdProp(testId)}
        >
            <Icon aria-hidden icon="gravity-ui:lock" />
            {t('chartOfAccounts.form.lockReason')}
        </span>
    );
};

/**
 * Render-only account create/edit form. Fields follow the §0 lock policy: the
 * structural fields (`type` / `normalBalance` / `isContra`) render disabled with
 * an inline reason once the account has postings; everything else stays
 * editable. All copy is localized; the drawer host supplies the title.
 */
export const AccountFormView: React.FC<AccountFormViewProps> = ({
    form,
    onCancel,
}) => {
    const { t } = useTranslation('pages');

    const codeErrorText =
        form.codeError === 'duplicate'
            ? t(CODE_ERROR_KEY.duplicate)
            : form.codeError === 'required' && form.submitted
              ? t(CODE_ERROR_KEY.required)
              : null;

    const nameErrorText =
        form.nameMissing && form.submitted
            ? t('chartOfAccounts.form.errors.nameRequired')
            : null;

    // A server rejection about the parent (`PARENT_NOT_FOUND`, `PARENT_CYCLE`,
    // `PARENT_HAS_POSTINGS`) belongs on the picker, not in the form-level
    // banner — it names the one control the user has to change. It outranks the
    // local "required" message because it is the more specific answer.
    const parentErrorText =
        form.parentError ??
        (form.parentMissing && form.submitted
            ? t('chartOfAccounts.form.errors.parentRequired')
            : null);

    const openingBalanceErrorText = form.openingBalanceErrorKey
        ? t(form.openingBalanceErrorKey)
        : null;

    const typeFrozen = form.frozenFields.has('type');
    const normalBalanceFrozen = form.frozenFields.has('normalBalance');
    const contraFrozen = form.frozenFields.has('isContra');

    const archiveReasonText =
        form.archiveGate && !form.archiveGate.canArchive
            ? form.archiveGate.reason === 'has-children'
                ? t('chartOfAccounts.form.archive.hasChildren')
                : t('chartOfAccounts.form.archive.requiresZeroBalance', {
                      amount: formatGel(form.archiveGate.balanceTetri),
                  })
            : null;

    // The by-id read failed, so there is nothing to edit: the fields would sit
    // empty and Save would answer "Enter a code" — a validation error for a form
    // that never loaded. Say what actually happened instead.
    if (form.detailError) {
        return (
            <div
                className="flex flex-col gap-3"
                {...getNodeTestIdProp('account-form')}
            >
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('account-form-detail-error')}
                >
                    {t('chartOfAccounts.form.errors.detailLoadFailed')}
                </p>
                <div className="flex items-center justify-end gap-3">
                    <Button
                        onPress={onCancel}
                        variant="tertiary"
                        {...getNodeTestIdProp('account-form-cancel')}
                    >
                        {t('chartOfAccounts.actions.cancel')}
                    </Button>
                    <Button
                        onPress={form.onRetryDetail}
                        variant="primary"
                        {...getNodeTestIdProp('account-form-detail-retry')}
                    >
                        {t('chartOfAccounts.error.retry')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div
            className="flex flex-col gap-5"
            {...getNodeTestIdProp('account-form')}
        >
            {/* Code */}
            <TextField
                className="flex flex-col gap-1"
                isDisabled={form.isLoadingDetail}
                isInvalid={codeErrorText !== null}
                onChange={form.onCodeChange}
                value={form.code}
                {...getNodeTestIdProp('account-form-code')}
            >
                <Label>{t('chartOfAccounts.form.fields.code')}</Label>
                <Input
                    className="tabular-nums"
                    inputMode="numeric"
                    placeholder={t(
                        'chartOfAccounts.form.fields.codePlaceholder',
                    )}
                />
                <FieldError className="text-xs text-danger">
                    {codeErrorText}
                </FieldError>
            </TextField>

            {/* Bilingual name */}
            <div className="flex flex-col gap-1">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <TextField
                        className="flex flex-col gap-1"
                        isDisabled={form.isLoadingDetail}
                        onChange={form.onNameKaChange}
                        value={form.nameKa}
                        {...getNodeTestIdProp('account-form-name-ka')}
                    >
                        <Label>{t('chartOfAccounts.form.fields.nameKa')}</Label>
                        <Input
                            placeholder={t(
                                'chartOfAccounts.form.fields.namePlaceholder',
                            )}
                        />
                    </TextField>
                    <TextField
                        className="flex flex-col gap-1"
                        isDisabled={form.isLoadingDetail}
                        onChange={form.onNameEnChange}
                        value={form.nameEn}
                        {...getNodeTestIdProp('account-form-name-en')}
                    >
                        <Label>{t('chartOfAccounts.form.fields.nameEn')}</Label>
                        <Input
                            placeholder={t(
                                'chartOfAccounts.form.fields.namePlaceholder',
                            )}
                        />
                    </TextField>
                </div>
                {nameErrorText ? (
                    <span
                        className="text-xs text-danger"
                        {...getNodeTestIdProp('account-form-name-error')}
                    >
                        {nameErrorText}
                    </span>
                ) : null}
            </div>

            {/* Parent */}
            <div className="flex flex-col gap-1">
                <Label id="account-form-parent-label">
                    {t('chartOfAccounts.form.fields.parent')}
                </Label>
                <AccountParentPicker
                    ariaLabel={t('chartOfAccounts.form.fields.parent')}
                    isDisabled={form.isLoadingDetail}
                    isInvalid={parentErrorText !== null}
                    onChange={form.onParentChange}
                    options={form.parentOptions}
                    placeholder={t(
                        'chartOfAccounts.form.fields.parentPlaceholder',
                    )}
                    value={form.parentId}
                />
                {parentErrorText ? (
                    <span
                        className="text-xs text-danger"
                        {...getNodeTestIdProp('account-form-parent-error')}
                    >
                        {parentErrorText}
                    </span>
                ) : null}
            </div>

            {/* Type + Normal balance */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1">
                    <Select
                        isDisabled={typeFrozen || form.isLoadingDetail}
                        onChange={(next) =>
                            next &&
                            form.onTypeChange(String(next) as AccountType)
                        }
                        placeholder={t('chartOfAccounts.form.fields.type')}
                        value={form.type}
                    >
                        <Label>{t('chartOfAccounts.form.fields.type')}</Label>
                        <Select.Trigger
                            {...getNodeTestIdProp('account-form-type')}
                        >
                            <Select.Value />
                            <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                            <ListBox>
                                {ACCOUNT_TYPE_OPTIONS.map((option) => (
                                    <ListBox.Item
                                        id={option.value}
                                        key={option.value}
                                        textValue={t(option.labelKey)}
                                    >
                                        {t(option.labelKey)}
                                        <ListBox.ItemIndicator />
                                    </ListBox.Item>
                                ))}
                            </ListBox>
                        </Select.Popover>
                    </Select>
                    {typeFrozen ? (
                        <LockNote testId="account-form-type-lock" />
                    ) : null}
                </div>

                <div className="flex flex-col gap-1">
                    <Select
                        isDisabled={normalBalanceFrozen || form.isLoadingDetail}
                        onChange={(next) =>
                            next &&
                            form.onNormalBalanceChange(
                                String(next) as NormalBalance,
                            )
                        }
                        placeholder={t(
                            'chartOfAccounts.form.fields.normalBalance',
                        )}
                        value={form.normalBalance}
                    >
                        <Label>
                            {t('chartOfAccounts.form.fields.normalBalance')}
                        </Label>
                        <Select.Trigger
                            {...getNodeTestIdProp(
                                'account-form-normal-balance',
                            )}
                        >
                            <Select.Value />
                            <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                            <ListBox>
                                {NORMAL_BALANCE_OPTIONS.map((option) => (
                                    <ListBox.Item
                                        id={option.value}
                                        key={option.value}
                                        textValue={t(option.labelKey)}
                                    >
                                        {t(option.labelKey)}
                                        <ListBox.ItemIndicator />
                                    </ListBox.Item>
                                ))}
                            </ListBox>
                        </Select.Popover>
                    </Select>
                    {normalBalanceFrozen ? (
                        <LockNote testId="account-form-normal-balance-lock" />
                    ) : null}
                </div>
            </div>

            {/* Is contra */}
            <div className="flex flex-col gap-1">
                <Switch
                    isDisabled={contraFrozen || form.isLoadingDetail}
                    isSelected={form.isContra}
                    onChange={form.onIsContraChange}
                    {...getNodeTestIdProp('account-form-contra')}
                >
                    <Switch.Content className="flex-row items-center gap-2">
                        <Switch.Control>
                            <Switch.Thumb />
                        </Switch.Control>
                        <Label className="text-sm text-foreground">
                            {t('chartOfAccounts.form.fields.isContra')}
                        </Label>
                    </Switch.Content>
                </Switch>
                {contraFrozen ? (
                    <LockNote testId="account-form-contra-lock" />
                ) : null}
            </div>

            {/* VAT role */}
            <div className="flex flex-col gap-1">
                <Select
                    isDisabled={form.isLoadingDetail}
                    onChange={(next) =>
                        next &&
                        form.onVatRoleChange(String(next) as VatRoleChoice)
                    }
                    placeholder={t('chartOfAccounts.form.fields.vatRole')}
                    value={form.vatRole}
                >
                    <Label>{t('chartOfAccounts.form.fields.vatRole')}</Label>
                    <Select.Trigger
                        {...getNodeTestIdProp('account-form-vat-role')}
                    >
                        <Select.Value />
                        <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                        <ListBox>
                            {VAT_ROLE_OPTIONS.map((option) => (
                                <ListBox.Item
                                    id={option.value}
                                    key={option.value}
                                    textValue={t(option.labelKey)}
                                >
                                    {t(option.labelKey)}
                                    <ListBox.ItemIndicator />
                                </ListBox.Item>
                            ))}
                        </ListBox>
                    </Select.Popover>
                </Select>
            </div>

            {/* Ledger-flow role */}
            <div className="flex flex-col gap-1">
                {/*
                 * `aria-describedby` is set explicitly rather than by nesting a
                 * FieldError or Description inside the Select. Both were tried:
                 * HeroUI's Select renders them outside its own root with no id
                 * and leaves the trigger with neither `aria-describedby` nor
                 * `aria-invalid`, so the message is visible but never announced.
                 * Pointing at a literal id is what actually associates the two.
                 */}
                <Select
                    aria-describedby={
                        form.roleHolder
                            ? ACCOUNT_ROLE_ERROR_ID
                            : ACCOUNT_ROLE_HINT_ID
                    }
                    isDisabled={form.isLoadingDetail}
                    isInvalid={form.roleHolder !== null}
                    onChange={(next) =>
                        next &&
                        form.onAccountRoleChange(
                            String(next) as AccountRoleChoice,
                        )
                    }
                    placeholder={t('chartOfAccounts.form.fields.accountRole')}
                    value={form.accountRole}
                >
                    <Label>
                        {t('chartOfAccounts.form.fields.accountRole')}
                    </Label>
                    <Select.Trigger
                        {...getNodeTestIdProp('account-form-account-role')}
                    >
                        <Select.Value />
                        <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                        <ListBox>
                            {ACCOUNT_ROLE_OPTIONS.map((option) => (
                                <ListBox.Item
                                    id={option.value}
                                    key={option.value}
                                    textValue={t(option.labelKey)}
                                >
                                    {t(option.labelKey)}
                                    <ListBox.ItemIndicator />
                                </ListBox.Item>
                            ))}
                        </ListBox>
                    </Select.Popover>
                </Select>
                {form.roleHolder ? (
                    <span
                        className="text-xs text-danger"
                        id={ACCOUNT_ROLE_ERROR_ID}
                        {...getNodeTestIdProp(
                            'account-form-account-role-error',
                        )}
                    >
                        {t('chartOfAccounts.form.errors.accountRoleTaken', {
                            code: form.roleHolder.code,
                            name: form.roleHolder.name,
                        })}
                    </span>
                ) : (
                    <span
                        className="text-xs text-muted"
                        id={ACCOUNT_ROLE_HINT_ID}
                    >
                        {t('chartOfAccounts.form.accountRoleHint')}
                    </span>
                )}
            </div>

            {/* Opening balance — creation only */}
            {form.mode === 'create' ? (
                <TextField
                    className="flex flex-col gap-1"
                    isInvalid={openingBalanceErrorText !== null}
                    onChange={form.onOpeningBalanceChange}
                    value={form.openingBalance}
                    {...getNodeTestIdProp('account-form-opening-balance')}
                >
                    <Label>
                        {t('chartOfAccounts.form.fields.openingBalance')}
                    </Label>
                    <Input
                        className="tabular-nums"
                        inputMode="decimal"
                        placeholder="0.00"
                    />
                    <FieldError className="text-xs text-danger">
                        {openingBalanceErrorText}
                    </FieldError>
                </TextField>
            ) : null}

            {/* The server refused a structural change on a posted account. The
                form disables those fields, so reaching this means the account
                gained postings while the drawer was open — say so on the fields
                it concerns rather than in the generic banner. */}
            {form.structuralError ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('account-form-structural-error')}
                >
                    {form.structuralError}
                </p>
            ) : null}

            {/* Someone else saved this account while it was open, and their edit
                touches a field this one does. Nothing has been discarded: the
                user's input is still in the fields above. This states what
                changed and what it now holds, so pressing Save again is a
                deliberate overwrite rather than a silent one. */}
            {form.conflicts.length > 0 ? (
                <div
                    className="flex flex-col gap-2 rounded-lg bg-warning-soft p-3"
                    {...getNodeTestIdProp('account-form-conflicts')}
                >
                    <span className="text-sm font-medium text-warning-soft-foreground">
                        {t('chartOfAccounts.form.conflict.title')}
                    </span>
                    <ul className="flex flex-col gap-1">
                        {form.conflicts.map((conflict) => (
                            <li
                                className="text-xs text-warning-soft-foreground"
                                key={conflict.labelKey}
                            >
                                {t('chartOfAccounts.form.conflict.field', {
                                    field: t(conflict.labelKey),
                                    value: conflict.theirValue,
                                })}
                            </li>
                        ))}
                    </ul>
                    <span className="text-xs text-warning-soft-foreground">
                        {t('chartOfAccounts.form.conflict.hint')}
                    </span>
                </div>
            ) : null}

            {/* Save error (form-level; input is preserved) */}
            {form.saveError ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('account-form-error')}
                >
                    {form.saveError}
                </p>
            ) : null}

            {/* Actions */}
            <div className="flex flex-col gap-3 border-t border-separator pt-4">
                {form.mode === 'edit' ? (
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-3">
                            {form.archived ? (
                                <Button
                                    isPending={form.isArchiving}
                                    onPress={form.onReactivate}
                                    variant="outline"
                                    {...getNodeTestIdProp(
                                        'account-form-reactivate',
                                    )}
                                >
                                    <Icon
                                        aria-hidden
                                        icon="gravity-ui:arrow-rotate-left"
                                    />
                                    {t('chartOfAccounts.actions.reactivate')}
                                </Button>
                            ) : (
                                <Button
                                    isDisabled={!form.archiveGate?.canArchive}
                                    isPending={form.isArchiving}
                                    onPress={form.onArchive}
                                    variant="danger-soft"
                                    {...getNodeTestIdProp(
                                        'account-form-archive',
                                    )}
                                >
                                    <Icon
                                        aria-hidden
                                        icon="gravity-ui:box-archive-in"
                                    />
                                    {t('chartOfAccounts.actions.archive')}
                                </Button>
                            )}
                        </div>
                        {!form.archived && archiveReasonText ? (
                            <span
                                className="text-xs text-muted"
                                {...getNodeTestIdProp(
                                    'account-form-archive-note',
                                )}
                            >
                                {archiveReasonText}
                            </span>
                        ) : null}
                        {form.archiveError ? (
                            <span
                                className="text-xs text-danger"
                                {...getNodeTestIdProp(
                                    'account-form-archive-error',
                                )}
                            >
                                {form.archiveError}
                            </span>
                        ) : null}
                    </div>
                ) : null}

                <div className="flex items-center justify-end gap-3">
                    <Button
                        isDisabled={form.isSaving}
                        onPress={onCancel}
                        variant="tertiary"
                        {...getNodeTestIdProp('account-form-cancel')}
                    >
                        {t('chartOfAccounts.actions.cancel')}
                    </Button>
                    <Button
                        isDisabled={form.isSaving || form.isLoadingDetail}
                        isPending={form.isSaving}
                        onPress={form.onSave}
                        variant="primary"
                        {...getNodeTestIdProp('account-form-save')}
                    >
                        {t('chartOfAccounts.actions.save')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
